/**
 * 文件夹管理页面
 * 创建、编辑、删除文件夹，支持层级结构
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout, Card, Button, Tree, Space, Modal, Form, Input,
  message, Popconfirm, Typography, Statistic, Row, Col, Select
} from 'antd'
import {
  FolderOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  ArrowLeftOutlined, FileTextOutlined, FolderOpenOutlined
} from '@ant-design/icons'
import type { DataNode } from 'antd/es/tree'
import { api } from '../services/api'
import './Folders.css'

const { Header, Content } = Layout
const { Title, Text } = Typography

interface FolderItem {
  id: number
  name: string
  description: string | null
  parent_id: number | null
  article_count: number
  created_at: string
}

const Folders: React.FC = () => {
  const navigate = useNavigate()
  const [folders, setFolders] = useState<FolderItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingFolder, setEditingFolder] = useState<FolderItem | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null)
  const [form] = Form.useForm()

  useEffect(() => {
    loadFolders()
  }, [])

  const loadFolders = async () => {
    try {
      setLoading(true)
      const response = await api.folders.list({ page_size: 100 })
      
      if (response.success) {
        setFolders(response.data || [])
      }
    } catch (error: any) {
      console.error('加载文件夹失败:', error)
      message.error('加载文件夹失败')
    } finally {
      setLoading(false)
    }
  }

  const buildTreeData = (folders: FolderItem[]): DataNode[] => {
    const folderMap = new Map<number, DataNode>()
    const rootFolders: DataNode[] = []

    // 创建所有节点
    folders.forEach(folder => {
      folderMap.set(folder.id, {
        key: folder.id,
        title: (
          <Space>
            <FolderOutlined style={{ color: 'var(--brand-fg)' }} />
            <Text strong>{folder.name}</Text>
            <Text type="secondary">({folder.article_count || 0})</Text>
          </Space>
        ),
        children: [],
        data: folder
      })
    })

    // 构建树形结构
    folders.forEach(folder => {
      const node = folderMap.get(folder.id)!
      if (folder.parent_id === null) {
        rootFolders.push(node)
      } else {
        const parent = folderMap.get(folder.parent_id)
        if (parent) {
          if (!parent.children) {
            parent.children = []
          }
          parent.children.push(node)
        } else {
          // 如果找不到父节点，作为根节点
          rootFolders.push(node)
        }
      }
    })

    return rootFolders
  }

  const handleCreate = () => {
    setEditingFolder(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (folder: FolderItem) => {
    setEditingFolder(folder)
    form.setFieldsValue({
      name: folder.name,
      description: folder.description,
      parent_id: folder.parent_id
    })
    setModalVisible(true)
  }

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        name: values.name,
        description: values.description,
        parent_id: values.parent_id || null
      }

      if (editingFolder) {
        // 更新文件夹
        const response = await api.folders.update(editingFolder.id, data)
        if (response.success) {
          message.success('更新成功')
        }
      } else {
        // 创建文件夹
        const response = await api.folders.create(data)
        if (response.success) {
          message.success('创建成功')
        }
      }

      setModalVisible(false)
      loadFolders()
    } catch (error: any) {
      console.error('保存文件夹失败:', error)
      message.error(editingFolder ? '更新失败' : '创建失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const response = await api.folders.delete(id)
      
      if (response.success) {
        message.success('删除成功')
        loadFolders()
        if (selectedFolder?.id === id) {
          setSelectedFolder(null)
        }
      }
    } catch (error: any) {
      console.error('删除文件夹失败:', error)
      message.error('删除失败')
    }
  }

  const handleSelect = (selectedKeys: React.Key[], info: any) => {
    if (selectedKeys.length > 0) {
      const folder = info.node.data as FolderItem
      setSelectedFolder(folder)
    } else {
      setSelectedFolder(null)
    }
  }

  const treeData = buildTreeData(folders)
  const totalArticles = folders.reduce((sum, folder) => sum + (folder.article_count || 0), 0)

  // 获取可选的父文件夹（排除自己和自己的子文件夹）
  const getAvailableParentFolders = () => {
    if (!editingFolder) return folders

    const excludeIds = new Set<number>([editingFolder.id])
    
    // 递归查找所有子文件夹
    const findChildren = (parentId: number) => {
      folders.forEach(folder => {
        if (folder.parent_id === parentId) {
          excludeIds.add(folder.id)
          findChildren(folder.id)
        }
      })
    }
    
    findChildren(editingFolder.id)
    
    return folders.filter(folder => !excludeIds.has(folder.id))
  }

  return (
    <Layout className="folders-layout">
      <Header className="folders-header">
        <div className="header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            type="text"
            style={{ color: 'white' }}
          >
            返回
          </Button>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            <FolderOutlined /> 文件夹管理
          </Title>
        </div>

        <div className="header-right">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ background: 'var(--surface)', color: 'var(--brand)' }}
          >
            新建文件夹
          </Button>
        </div>
      </Header>

      <Content className="folders-content">
        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="文件夹总数"
                value={folders.length}
                prefix={<FolderOutlined />}
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="关联文章"
                value={totalArticles}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="平均文章数"
                value={folders.length > 0 ? (totalArticles / folders.length).toFixed(1) : 0}
                suffix="篇/文件夹"
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* 文件夹树 */}
          <Col xs={24} lg={12}>
            <Card title="文件夹结构" className="folder-tree-card">
              {treeData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FolderOpenOutlined style={{ fontSize: 48, color: '#ccc' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary">还没有创建任何文件夹</Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    style={{ marginTop: 16 }}
                  >
                    创建第一个文件夹
                  </Button>
                </div>
              ) : (
                <Tree
                  showLine
                  showIcon
                  defaultExpandAll
                  treeData={treeData}
                  onSelect={handleSelect}
                  style={{ background: 'transparent' }}
                />
              )}
            </Card>
          </Col>

          {/* 文件夹详情 */}
          <Col xs={24} lg={12}>
            <Card title="文件夹详情" className="folder-detail-card">
              {selectedFolder ? (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Text type="secondary">名称</Text>
                    <Title level={4} style={{ marginTop: 8 }}>
                      <FolderOutlined style={{ color: 'var(--brand-fg)', marginRight: 8 }} />
                      {selectedFolder.name}
                    </Title>
                  </div>

                  {selectedFolder.description && (
                    <div>
                      <Text type="secondary">描述</Text>
                      <div style={{ marginTop: 8 }}>
                        <Text>{selectedFolder.description}</Text>
                      </div>
                    </div>
                  )}

                  <div>
                    <Text type="secondary">文章数量</Text>
                    <div style={{ marginTop: 8 }}>
                      <Space>
                        <FileTextOutlined />
                        <Text strong>{selectedFolder.article_count || 0} 篇文章</Text>
                      </Space>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary">创建时间</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text>{new Date(selectedFolder.created_at).toLocaleString('zh-CN')}</Text>
                    </div>
                  </div>

                  <Space style={{ marginTop: 16 }}>
                    <Button
                      icon={<FileTextOutlined />}
                      onClick={() => navigate(`/articles?folder_id=${selectedFolder.id}`)}
                    >
                      查看关联文章
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(selectedFolder)}
                    >
                      编辑
                    </Button>
                    <Popconfirm
                      title="确认删除"
                      description={`确定要删除文件夹"${selectedFolder.name}"吗？`}
                      onConfirm={() => handleDelete(selectedFolder.id)}
                      okText="确认"
                      cancelText="取消"
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FolderOpenOutlined style={{ fontSize: 48, color: '#ccc' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary">选择一个文件夹查看详情</Text>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Content>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingFolder ? '编辑文件夹' : '新建文件夹'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="文件夹名称"
            rules={[
              { required: true, message: '请输入文件夹名称' },
              { max: 50, message: '文件夹名称最多50个字符' }
            ]}
          >
            <Input placeholder="输入文件夹名称" prefix={<FolderOutlined />} />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              placeholder="输入文件夹描述（可选）"
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="parent_id"
            label="父文件夹"
          >
            <Select
              placeholder="选择父文件夹（可选）"
              allowClear
            >
              {getAvailableParentFolders().map(folder => (
                <Select.Option key={folder.id} value={folder.id}>
                  {folder.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

export default Folders

